import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, CreditCard, Map, Bell, LogOut, NavArrowRight, Bin, Plus, Whatsapp, Notes, Gift } from 'iconoir-react';
import { Offcanvas, Form, Button, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Lottie from 'lottie-react';
import cartaoCreditoAnimation from '../assets/cartao-credito.json';
import locationAnimation from '../assets/Location.json';
import { getClienteProfile, removerEndereco, getCartoes, removerCartao, updateClienteProfile } from '../services/api';
import { getCardBrand, getCardBrandLogo, getCardBrandName } from '../utils/cardBrand';
import AdicionarEnderecoModal from './AdicionarEnderecoModal';
import MeusPedidosOffcanvas from './MeusPedidosOffcanvas';
import LoyaltyPage from './LoyaltyPage';
import SuccessToast from './SuccessToast';
import { APP_VERSION } from '../config/version';
import './ButtonStyles.css';

function PerfilOffcanvas({ show, onHide, usuario, onLogout, brand, onUserUpdate, pedidosNaoEntregues = 0 }) {
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showEnderecoModal, setShowEnderecoModal] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [notificacoesAtivas, setNotificacoesAtivas] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: ''
  });
  const [originalData, setOriginalData] = useState({
    nome: '',
    telefone: '',
    email: ''
  });
  const [cartoes, setCartoes] = useState([]);
  const [cartoesLoading, setCartoesLoading] = useState(false);
  const [cartoesError, setCartoesError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMeusPedidos, setShowMeusPedidos] = useState(false);

  useEffect(() => {
    if (show) {
      // Verificar se há token no localStorage mesmo se usuario não estiver definido
      const token = localStorage.getItem('sitri_token');
      if (token || usuario) {
        carregarPerfil();
        if (usuario) {
          const userData = {
            nome: usuario.nome || '',
            telefone: usuario.telefone || '',
            email: usuario.email || ''
          };
          setFormData(userData);
          setOriginalData(userData);
        }
        setCurrentView('home');
      }
    }
  }, [show, usuario]);

  useEffect(() => {
    if (currentView === 'cartoes' && show) {
      carregarCartoes();
    }
  }, [currentView, show]);

  const carregarPerfil = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        const resultado = await getClienteProfile(token);
        if (resultado && resultado.success) {
          setPerfil(resultado);
          
          // Se o usuário não estiver definido no estado, definir com os dados do perfil
          if (!usuario && resultado.usuario) {
            const userData = resultado.usuario;
            // Atualizar localStorage e notificar componente pai
            localStorage.setItem('sitri_user', JSON.stringify(userData));
            
            // Notificar componente pai sobre a atualização do usuário
            if (onUserUpdate) {
              onUserUpdate(userData);
            }
            
            // Atualizar formData com os dados carregados
            const userFormData = {
              nome: userData.nome || '',
              telefone: userData.telefone || '',
              email: userData.email || ''
            };
            setFormData(userFormData);
            setOriginalData(userFormData);
          }
        }
      }
    } catch (error) {
      setError('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhone = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{2})(\d{5})(\d{4})/, '($1)$2-$3');
  };

  const salvarAlteracoes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('sitri_token');
      if (!token) {
        setError('Token não encontrado');
        return;
      }

      const [resultado] = await Promise.all([
        updateClienteProfile(token, {
          nome: formData.nome,
          telefone: formData.telefone,
          email: formData.email
        }),
        new Promise(resolve => setTimeout(resolve, 500))
      ]);

      if (resultado && resultado.success) {
        // Atualizar dados locais
        const updatedUser = {
          ...usuario,
          nome: formData.nome,
          telefone: formData.telefone,
          email: formData.email
        };
        
        // Salvar no localStorage se necessário
        localStorage.setItem('sitri_user', JSON.stringify(updatedUser));
        
        setEditMode(false);
        setSuccessMessage('Alterações salvas com sucesso!');
        setShowSuccessToast(true);
        
        // Recarregar perfil para garantir dados atualizados
        carregarPerfil();
      } else {
        setError(resultado?.message || 'Erro ao salvar alterações');
      }
    } catch (error) {
      setError('Erro ao salvar alterações');
    } finally {
      setLoading(false);
    }
  };

  const carregarCartoes = async () => {
    setCartoesLoading(true);
    setCartoesError('');
    try {
      const token = localStorage.getItem('sitri_token');
      if (token) {
        const resultado = await getCartoes(token);
        if (resultado && resultado.success) {
          setCartoes(resultado.data || []);
        } else {
          setCartoesError('Erro ao carregar cartões');
        }
      }
    } catch (error) {
      setCartoesError('Erro ao carregar cartões');
    } finally {
      setCartoesLoading(false);
    }
  };

  const handleRemoverCartao = async (cartao) => {
    try {
      const token = localStorage.getItem('sitri_token');
      const resultado = await removerCartao(token, cartao.id);
      if (resultado && resultado.success) {
        setSuccessMessage('Cartão removido com sucesso!');
        setShowSuccessToast(true);
        carregarCartoes();
      } else {
        setCartoesError(resultado?.message || 'Erro ao remover cartão');
      }
    } catch (error) {
      setCartoesError('Erro ao remover cartão. Tente novamente.');
    }
  };

  const handleRemoverEndereco = async (endereco) => {
    try {
      const token = localStorage.getItem('sitri_token');
      const resultado = await removerEndereco(token, endereco.id);
      if (resultado && resultado.success) {
        setSuccessMessage('Endereço removido com sucesso!');
        setShowSuccessToast(true);
        carregarPerfil();
      } else {
        setError(resultado?.message || 'Erro ao remover endereço');
      }
    } catch (error) {
      setError('Erro ao remover endereço. Tente novamente.');
    }
  };



  const handleAbrirMeusPedidos = () => {
    onHide();
    setTimeout(() => {
      setShowMeusPedidos(true);
    }, 300);
  };

  const handleLogout = () => {
    // Limpar todas as flags relacionadas ao usuário
    localStorage.removeItem('sitri_user_data_updated');
    localStorage.removeItem('sitri_force_user_data');
    onLogout();
    onHide();
  };

  const handleEnderecoAdicionado = () => {
    carregarPerfil();
    setShowEnderecoModal(false);
  };

  // Verificar se usuário está logado com Google
  const isGoogleLogin = usuario?.is_google_login || perfil?.usuario?.is_google_login;

  // Verificar se há token no localStorage mesmo se usuario não estiver definido
  const token = localStorage.getItem('sitri_token');
  if (!usuario && !token) return null;

  return (
    <>
    <Offcanvas 
      show={show} 
      onHide={onHide} 
      placement="end" 
      style={{ width: "40%" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="w-100">
          <div className="d-flex align-items-center justify-content-between w-100">
            <div className="d-flex align-items-center" style={{gap:'8px'}}>
              {(currentView !== 'home' || editMode) && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => { setCurrentView('home'); setEditMode(false); }}
                  className="btn-cancel-light"
                  style={{
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    border: '1px solid #e5e7eb',
                    color: '#6b7280',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <ArrowLeft width="16" height="16" />
                  Voltar
                </Button>
              )}
              <h5 className="mb-0" style={{fontWeight:700}}>{currentView === 'enderecos' ? 'Meus endereços' : currentView === 'cartoes' ? 'Meus cartões' : currentView === 'fidelidade' ? 'Clube de Fidelidade' : editMode ? 'Editar Perfil' : 'Meu Perfil'}</h5>
            </div>
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      
      <Offcanvas.Body className="bg-light" style={{backgroundColor:'#f3f4f6'}}>
        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="py-2">
            <small>{error}</small>
          </Alert>
        )}

        {!loading && (
          <>
            <div className="container pt-3">
              {!editMode && currentView === 'home' ? (
                <>
                  <div className="p-3 mb-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', cursor:'pointer'}} onClick={() => setEditMode(true)}>
                    <div className="d-flex align-items-center justify-content-between" style={{gap:'12px'}}>
                      <div className="d-flex align-items-center" style={{gap:'12px'}}>
                      <div style={{width:'48px', height:'48px', borderRadius:'50%', background:'#f9fafb', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280', position:'relative'}}>
                        <User />
                        {(usuario?.is_google_login || perfil?.usuario?.is_google_login) && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip>
                                Login feito na GRUB utilizando um email do Google
                              </Tooltip>
                            }
                          >
                            <img 
                              src="https://grubicons.s3.us-east-1.amazonaws.com/google-icon.avif"
                              alt="Google"
                              style={{
                                position: 'absolute',
                                bottom: '-3px',
                                right: '-10px',
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                border: '2px solid white',
                                backgroundColor: 'white',
                                cursor: 'pointer'
                              }}
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                      <div>
                        <div className="fw-bold" style={{fontSize:'0.95rem', color:'#111827'}}>{usuario?.nome || perfil?.usuario?.nome || 'Usuário'}</div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>{usuario?.email || perfil?.usuario?.email || 'Sem e-mail'}</div>
                      </div>
                      </div>
                      <NavArrowRight style={{color:'#9ca3af'}} />
                    </div>
                  </div>

                  <h6 className="mb-2" style={{fontSize:'0.95rem', fontWeight:600, color:'#1a1a1a'}}>Geral</h6>
                  <div className="mb-3">
                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', marginBottom:'12px', cursor:'pointer'}} onClick={handleAbrirMeusPedidos}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280', position:'relative'}}>
                        <Notes />
                        {pedidosNaoEntregues > 0 && (
                          <div style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            boxShadow: '0 0 0 0 rgba(0, 123, 255, 0.7)',
                            animation: 'pulse-blue 2s ease-in-out infinite'
                          }}>
                            {pedidosNaoEntregues}
                          </div>
                        )}
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>Meus Pedidos</div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Histórico de pedidos realizados</div>
                      </div>
                      <NavArrowRight style={{color:'#9ca3af'}} />
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', marginBottom:'12px', cursor:'pointer'}} onClick={() => setCurrentView('fidelidade')}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                        <Gift />
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>Clube de Fidelidade</div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Pontos, benefícios e extrato</div>
                      </div>
                      <NavArrowRight style={{color:'#9ca3af'}} />
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', marginBottom:'12px', cursor:'pointer'}} onClick={() => setCurrentView('cartoes')}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                        <CreditCard />
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>Meus Cartões</div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Gerenciar formas de pagamento salvas</div>
                      </div>
                      <NavArrowRight style={{color:'#9ca3af'}} />
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', marginBottom:'12px', cursor:'pointer'}} onClick={() => setCurrentView('enderecos')}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                        <Map />
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>Meus Endereços</div>
                        <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Adicionar, remover e escolher endereços</div>
                      </div>
                      <NavArrowRight style={{color:'#9ca3af'}} />
                    </div>
                  </div>

                  <h6 className="mb-2" style={{fontSize:'0.95rem', fontWeight:600, color:'#1a1a1a'}}>Preferências</h6>
                  <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center" style={{gap:'12px'}}>
                        <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                          <Whatsapp />
                        </div>
                        <div>
                          <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>Notificações</div>
                          <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Receber atualizações por WhatsApp</div>
                        </div>
                      </div>
                      <Form.Check type="switch" checked={notificacoesAtivas} onChange={(e)=>setNotificacoesAtivas(e.target.checked)} />
                    </div>
                  </div>

                  <div className="d-flex align-items-center p-3 mt-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)', cursor:'pointer'}} onClick={handleLogout}>
                    <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#ef4444'}}>
                      <LogOut />
                    </div>
                    <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                      <div className="fw-bold" style={{fontSize:'0.9rem', color:'#ef4444'}}>Sair da Conta</div>
                      <div style={{color:'#6b7280', fontSize:'0.8rem'}}>Encerrar sessão atual</div>
                    </div>
                    <NavArrowRight style={{color:'#fca5a5'}} />
                  </div>

                  <div className="text-center mt-5" style={{color:'#9ca3af', fontSize:'0.75rem'}}>
                    Versão aplicação: {APP_VERSION}
                  </div>
                </>
              ) : editMode ? (
                <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="mb-0" style={{fontSize:'0.95rem', fontWeight:600, color:'#1a1a1a'}}>Editar Perfil</h6>
                  </div>
                  <Form>
                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'12px'}}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280', position:'relative'}}>
                        <User />
                        {isGoogleLogin && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Dado vinculado ao Google</Tooltip>}
                          >
                            <img 
                              src="https://grubicons.s3.us-east-1.amazonaws.com/google-icon.avif"
                              alt="Google"
                              style={{
                                position: 'absolute',
                                bottom: '-3px',
                                right: '-3px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: '2px solid white',
                                backgroundColor: 'white'
                              }}
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="small text-muted mb-1">Nome</div>
                        <Form.Control 
                          type="text" 
                          value={formData.nome} 
                          onChange={(e)=>handleInputChange('nome', e.target.value)} 
                          disabled={isGoogleLogin}
                          size="sm" 
                        />
                        {isGoogleLogin && (
                          <small className="text-muted" style={{fontSize:'0.75rem'}}>Nome fornecido no login do Google não pode ser alterado.</small>
                        )}
                      </div>
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'12px'}}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280', position:'relative'}}>
                        <User />
                        {isGoogleLogin && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Dado vinculado ao Google</Tooltip>}
                          >
                            <img 
                              src="https://grubicons.s3.us-east-1.amazonaws.com/google-icon.avif"
                              alt="Google"
                              style={{
                                position: 'absolute',
                                bottom: '-3px',
                                right: '-3px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                border: '2px solid white',
                                backgroundColor: 'white'
                              }}
                            />
                          </OverlayTrigger>
                        )}
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="small text-muted mb-1">Email</div>
                        <Form.Control 
                          type="email" 
                          value={formData.email} 
                          onChange={(e)=>handleInputChange('email', e.target.value)} 
                          disabled={isGoogleLogin}
                          size="sm" 
                        />
                        {isGoogleLogin && (
                          <small className="text-muted" style={{fontSize:'0.75rem'}}>Email fornecido no login do Google não pode ser alterado.</small>
                        )}
                      </div>
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'12px'}}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                        <User />
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="small text-muted mb-1">CPF</div>
                        <Form.Control type="text" value={usuario?.cpf || perfil?.usuario?.cpf || ''} disabled size="sm" />
                      </div>
                    </div>

                    <div className="d-flex align-items-center p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'16px'}}>
                      <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb', color:'#6b7280'}}>
                        <Whatsapp />
                      </div>
                      <div className="flex-grow-1" style={{marginLeft:'12px'}}>
                        <div className="small text-muted mb-1">Telefone</div>
                        <Form.Control type="text" value={formatPhone(formData.telefone)} onChange={(e)=>handleInputChange('telefone', e.target.value.replace(/\D/g, ''))} size="sm" />
                      </div>
                    </div>

                    <div className="d-flex" style={{gap:'12px'}}>
                      <Button 
                        variant="outline-secondary" 
                        onClick={()=>setEditMode(false)} 
                        disabled={loading}
                        className="flex-grow-1 btn-cancel-light"
                        style={{
                          padding: '12px',
                          borderRadius: '12px',
                          fontSize: '0.9rem',
                          border: '1px solid #e5e7eb',
                          color: '#6b7280',
                          backgroundColor: 'white'
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        variant="primary" 
                        onClick={salvarAlteracoes} 
                        disabled={loading || (formData.nome === originalData.nome && formData.telefone === originalData.telefone && formData.email === originalData.email) || (isGoogleLogin && formData.telefone === originalData.telefone)}
                        className="flex-grow-1 btn-primary-glow"
                        style={{
                          padding: '12px',
                          borderRadius: '12px',
                          backgroundColor: loading ? '#0056cc' : '#006affff',
                          border: 'none',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          boxShadow: '0 4px 12px rgba(0,106,255,0.25)',
                          opacity: (formData.nome === originalData.nome && formData.telefone === originalData.telefone && formData.email === originalData.email) || (isGoogleLogin && formData.telefone === originalData.telefone) ? 0.5 : 1
                        }}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Salvando...
                          </>
                        ) : (
                          'Salvar Alterações'
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              ) : currentView === 'enderecos' ? (
                <div>
                 
                  {perfil?.enderecos && perfil.enderecos.length > 0 ? (
                    perfil.enderecos.map((endereco, index) => (
                      <div key={index} className="d-flex align-items-center justify-content-between p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'8px'}}>
                        <div className="d-flex" style={{gap:'12px'}}>
                          <div style={{width:'36px', height:'36px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb'}}>
                            <Map style={{color:'#6b7280'}} />
                          </div>
                          <small>
                            {endereco.rua}, {endereco.numero} - {endereco.bairro}, {endereco.cidade}
                            {endereco.cep ? `\nCEP: ${endereco.cep}` : ''}
                          </small>
                        </div>
                        {perfil.enderecos.length > 1 && (
                          <Button variant="outline-danger" size="sm" onClick={()=>handleRemoverEndereco(endereco)}>
                            <Bin />
                          </Button>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <div style={{ width: '160px', height: '160px', margin: '0 auto' }}>
                        <Lottie animationData={locationAnimation} loop={true} />
                      </div>
                      <div style={{ color: '#111827', fontWeight: 700, fontSize: '0.95rem' }}>Ops, nenhum endereço cadastrado por aqui</div>
                      <div className="text-muted" style={{ fontSize: '0.85rem' }}>Adicione um endereço para realizar pedidos com entrega.</div>
                    </div>
                  )}
                  <Button 
                    variant="primary" 
                    onClick={()=>setShowEnderecoModal(true)}
                    className="w-100 btn-primary-glow"
                    style={{
                      padding: '12px',
                      borderRadius: '12px',
                      backgroundColor: '#0039ff',
                      border: 'none',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 12px rgba(0,106,255,0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginTop: '20px'
                    }}
                  >
                    <Plus width="18" height="18" />
                    Adicionar Endereço
                  </Button>
                </div>
              ) : currentView === 'cartoes' ? (
                <div>
                 
                  {cartoesError && (
                    <Alert variant="danger" className="py-2 mb-2"><small>{cartoesError}</small></Alert>
                  )}
                  {cartoesLoading ? (
                    <div className="text-center py-3">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : cartoes.length > 0 ? (
                    cartoes.map((cartao) => {
                      // Usar a bandeira que já vem do backend, ou detectar se não vier
                      const backendBrand = cartao.extra_info?.creditCardBrand;
                      const detectedBrand = (backendBrand && backendBrand !== 'UNKNOWN') ? backendBrand : 'UNKNOWN';
                      const brandLogo = getCardBrandLogo(detectedBrand);
                      const brandName = getCardBrandName(detectedBrand);
                      const lastFour = cartao.extra_info?.creditCardNumber || '****';
                      
                      return (
                      <div key={cartao.id} className="d-flex align-items-center justify-content-between p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', marginBottom:'8px'}}>
                        <div className="d-flex align-items-center" style={{gap:'12px'}}>
                          <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb'}}>
                            {brandLogo ? (
                              <img src={brandLogo} alt={brandName} style={{width:'32px', height:'auto'}} />
                            ) : (
                              <CreditCard style={{color:'#6b7280'}} />
                            )}
                          </div>
                          <div>
                            <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>
                              {brandName} **** {lastFour}
                            </div>
                            <div className="text-muted small">Adicionado em {new Date(cartao.created_at).toLocaleDateString('pt-BR')}</div>
                          </div>
                        </div>
                        {cartoes.length > 1 && (
                          <div className="d-flex" style={{gap:'8px'}}>
                            <Button variant="outline-danger" size="sm" onClick={()=>handleRemoverCartao(cartao)}>
                              <Bin className="me-1" /> Remover
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                    })
                  ) : (
                    <div className="text-center py-4">
                      <div style={{ width: '260px', height: '160px', margin: '0 auto' }}>
                        <Lottie animationData={cartaoCreditoAnimation} loop={true} />
                      </div>
                      <div style={{ color: '#111827', fontWeight: 700, fontSize: '0.95rem' }}>
  Ops, nenhum cartão cadastrado por aqui
</div>
<div className="text-muted" style={{ fontSize: '0.85rem' }}>
  Ao finalizar a primeira compra com cartão de crédito, você poderá salvá-lo para futuras compras.
</div>

                    </div>
                  )}
                </div>
              ) : currentView === 'fidelidade' ? (
                <LoyaltyPage brand={brand} token={localStorage.getItem('sitri_token')} />
              ) : null}
            </div>
          </>
        )}

        <AdicionarEnderecoModal
          show={showEnderecoModal}
          onHide={() => setShowEnderecoModal(false)}
          brand={brand}
          onEnderecoAdicionado={handleEnderecoAdicionado}
        />
        
        <SuccessToast
          show={showSuccessToast}
          message={successMessage}
          onHide={() => setShowSuccessToast(false)}
        />
        
      </Offcanvas.Body>
    </Offcanvas>
    
    <MeusPedidosOffcanvas
      show={showMeusPedidos}
      onHide={() => setShowMeusPedidos(false)}
      usuario={usuario}
      brand={brand}
    />
    </>
  );
}

export default PerfilOffcanvas;
